import React, { useEffect, useState } from "react";
import T from "prop-types";
import { Paginator } from "@orange-marmalade/paginate-this";
import { connect } from "react-redux";
import {
  FilterSelect,
  TitleWithFilters
} from "MODULES/shared/components/Pagination";
import { Search as NotificationSearch } from "MODULES/shared/components";
import { ADMIN, ACCOUNTING, LAST_NAME } from "MODULES/users/constants";
import { matchesBoolean, matchesInt } from "LIB/filters";
import api from "APP_ROOT/api";
import { useCurrentUser } from "APP_ROOT/hooks";
import { isEmpty } from "lodash";
import Table from "./Table";
import MarkAllRead from "./MarkAllRead/MarkAllRead";
import pagination from "../../pagination";
import PaginationController from "./PaginationController";

const defaultFilters = {
  all_oqs_quoted_3_times: undefined,
  company: undefined,
  customer_rma: undefined,
  part: undefined,
  incoming_quote: undefined,
  inventory: undefined,
  outgoing_quote: undefined,
  purchase_order: undefined,
  received_order: undefined,
  repair: undefined,
  sales_admin: undefined,
  sales_order: undefined,
  vendor_rma: undefined,
  filter: undefined
};

const NotificationIcon = ({ icon, title }) => (
  <i className={`fa fa-${icon}`}>
    <span
      style={{
        fontFamily: `'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif`
      }}
    >
      {" "}
      - {title}
    </span>
  </i>
);

NotificationIcon.propTypes = {
  icon: T.string.isRequired,
  title: T.string.isRequired
};

const UrlIcon = ({ url, title }) => (
  <div style={{ display: "inline-block" }}>
    <img src={url} height="17px" alt={title} />
    <span>&nbsp;-&nbsp;{title}</span>
  </div>
);

UrlIcon.propTypes = {
  url: T.string.isRequired,
  title: T.string.isRequired
};

export const filters = [
  {
    key: "part",
    label: <NotificationIcon title="Parts" icon="cogs" />,
    filters: { ...defaultFilters, part: true }
  },
  {
    key: "incoming_quote",
    label: <NotificationIcon icon="quote-left" title="Incoming Quotes" />,
    filters: { ...defaultFilters, incoming_quote: true }
  },
  {
    key: "inventory",
    label: (
      <UrlIcon
        url="https://aero-zone-assets.s3.amazonaws.com/inventory.png"
        title="Inventory"
      />
    ),
    filters: { ...defaultFilters, inventory: true }
  },
  {
    key: "outgoing_quote",
    label: <NotificationIcon icon="quote-right" title="Outgoing Quotes" />,
    filters: { ...defaultFilters, outgoing_quote: true }
  },
  {
    key: "purchase_order",
    label: <NotificationIcon icon="shopping-cart" title="Purchase Orders" />,
    filters: { ...defaultFilters, purchase_order: true }
  },
  {
    key: "sales_order",
    label: <NotificationIcon icon="dollar" title="Sales Orders" />,
    filters: { ...defaultFilters, sales_order: true }
  },
  {
    key: "repair",
    label: <NotificationIcon icon="wrench" title="Repairs" />,
    filters: { ...defaultFilters, repair: true }
  },
  {
    key: "customer_rma",
    label: <NotificationIcon icon="cog" title="Customer RMAs" />,
    filters: { ...defaultFilters, customer_rma: true }
  },
  {
    key: "vendor_rma",
    label: <NotificationIcon icon="shield" title="Vendor RMAs" />,
    filters: { ...defaultFilters, vendor_rma: true }
  },
  {
    key: "company",
    label: <NotificationIcon icon="building" title="Companies" />,
    filters: { ...defaultFilters, company: true }
  },
  {
    key: "received_order",
    label: <NotificationIcon icon="truck" title="Received Orders" />,
    filters: { ...defaultFilters, received_order: true }
  },
  {
    key: "sales_admin",
    label: <NotificationIcon icon="vcard" title="Sales Admin Notifications" />,
    filters: { ...defaultFilters, sales_admin: true },
    withAuthorization: { roles: [ADMIN, ACCOUNTING] }
  },
  {
    key: "read",
    label: "Read",
    filters: { read: true }
  },
  {
    key: "read",
    label: "Unread",
    filters: { read: false }
  },
  {
    key: "all",
    label: "All Notifications",
    filters: { ...defaultFilters }
  }
];

const filterTags = [
  {
    label: "Show Parts",
    key: "part"
  },
  {
    label: "Show Incoming Quotes",
    key: "incoming_quote"
  },
  {
    label: "Show Inventory",
    key: "inventory"
  },
  {
    label: "Show Outgoing Quotes",
    key: "outgoing_quote"
  },
  {
    label: "Show Purchase Orders",
    key: "purchase_order"
  },
  {
    label: "Show Sales Orders",
    key: "sales_order"
  },
  {
    label: "Show Repairs",
    key: "repair"
  },
  {
    label: "Show Customer RMA and Warranties",
    key: "customer_rma"
  },
  {
    label: "Show Received Orders",
    key: "received_order"
  },
  {
    label: "Show Companies",
    key: "company"
  },
  {
    label: "Show Vendor Rmas",
    key: "vendor_rma"
  },
  {
    label: "Sales Admin Notifications",
    key: "sales_admin"
  },
  {
    label: "Read",
    key: "read",
    matchesTarget: matchesBoolean(true)
  },
  {
    label: "Unread",
    key: "read",
    matchesTarget: matchesBoolean(false)
  }
];

function Search() {
  const currentUser = useCurrentUser();
  const [users, setUsers] = useState([]);
  useEffect(() => {
    api.users
      .search({ active: true, limit: 1000, sort: LAST_NAME, reverse: false })
      .then(({ data }) => {
        setUsers(data.results);
        return function cleanup() {};
      });
  }, []);

  return (
    <NotificationSearch search={pagination} syncWithUrl>
      {searchProps => {
        const filtersSet = searchProps.paginator.toJS().filters;
        return (
          <div>
            <PaginationController {...searchProps} limit={20} />
            <div style={{ display: "inline-block" }}>
              <TitleWithFilters
                {...searchProps}
                title={"Notifications"}
                filters={filterTags.concat(
                  users.map(u => ({
                    key: "user_id",
                    label: u.full_name,
                    matchesTarget: matchesInt(u.id)
                  }))
                )}
              />
            </div>
            <div className="form-group-searchbar" style={{ float: "right" }}>
              <div className="input-group">
                <div style={{ float: "right", paddingRight: "30px" }}>
                  {isEmpty(filtersSet.user_id) && (
                    <MarkAllRead filters={filtersSet} {...searchProps} />
                  )}
                  {currentUser.role === ADMIN && (
                    <FilterSelect
                      {...searchProps}
                      filters={users.map(u => ({
                        key: "user_id",
                        label: u.full_name,
                        filters: { user_id: u.id }
                      }))}
                      title="User"
                      right
                    />
                  )}
                  <FilterSelect {...searchProps} filters={filters} right />
                </div>
              </div>
            </div>
            <Table {...searchProps} filtersSet={filtersSet} />
            <Paginator {...searchProps} listId="searchNotifications" />
          </div>
        );
      }}
    </NotificationSearch>
  );
}

export default connect(undefined)(Search);



// WEBPACK FOOTER //
// ./app/modules/Notifications/components/Search/Search.jsx